/* eslint-disable @typescript-eslint/consistent-type-assertions */

// from Smartsuite frontend/projects/main/src/app/shared-with-mobile-app/application-structure/fields/file-field/file.utils.ts ~line 148
export const getFileType = (mimetype: string): FileType =>
  mimeTypesMap[mimetype] ?? FILE_TYPE_OTHER;

const FILE_TYPE_OTHER = "other";

type FileType =
  | "ai"
  | "archive"
  | "cad"
  | "card"
  | "csv"
  | "csv-color"
  | "dwg"
  | "dxf"
  | "eps"
  | "gif"
  | "gsheet"
  | "ics"
  | "icx"
  | "image"
  | "misc"
  | "audio"
  | "music" // fixme: check if we ever use this type (@paxapy)
  | "other"
  | "pdf"
  | "powerpoint"
  | "ps"
  | "psd"
  | "sketch"
  | "spreadsheet"
  | "svg"
  | "text"
  | "tiff"
  | "ttf"
  | "vc"
  | "vcf"
  | "vcard"
  | "video"
  | "word"
  | "xml"
  | "xps";

const ImageMimeTypes = [
  "image/jpeg",
  "image/png",
  "image/bmp",
  "image/pjpeg",
  "image/vnd.microsoft.icon",
  "image/vnd.wap.wbmp",
  "image/webp",
  "image/heif",
  "image/heic",
];
const GifMimeTypes = ["image/gif"];
const VideoMimeTypes = [
  "video/webm",
  "video/mpeg",
  "video/mp4",
  "video/x-flv",
  "video/3gpp",
  "video/3gpp2",
  "video/ogg",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-ms-wmv",
  "video/mp2t",
  "video/MP2P",
  "video/MP1S",
  "video/avi",
  "application/x-mpeg",
  "video/x-msvideo",
  "video/x-ms-wmv",
];
const AudioMimeTypes = [
  "audio/mp3",
  "audio/aac",
  "audio/midi",
  "audio/x-midi",
  "audio/mpeg",
  "audio/ogg",
  "audio/wav",
  "audio/webm",
  "audio/3gpp",
  "audio/3gpp2",
  "audio/basic",
  "audio/L24",
  "audio/mp4",
  "audio/vorbis",
  "audio/x-ms-wma",
  "audio/x-ms-wax",
  "audio/x-wav",
  "audio/vnd.rn",
  "audio/vnd.wave",
];
const WordMimeTypes = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const PowerpointMimeTypes = [
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.presentationml.slideshow",
  "application/vnd.ms-powerpoint.addin.macroEnabled.12",
];
const SpreadsheetMimeTypes = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
];
const OpenDocumentMimeTypes = [
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
  "application/vnd.oasis.opendocument.graphics",
];
const ArchiveMimeTypes = [
  "application/vnd.rar",
  "application/x-rar",
  "application/x-rar-compressed",
  "application/octet-stream",
  "application/zip",
  "application/x-zip-compressed",
  "multipart/x-zip",
  "application/gzip",
  "application/tar",
  "application/tar+gzip",
  "application/x-gzip",
  "application/x-gtar",
  "application/x-tgz",
  "application/x-tar",
  "application/x-bzip",
  "application/x-bzip2",
  "application/x-7z-compressed",
];
const PdfMimeTypes = ["application/pdf"];
const XmlMimeTypes = ["application/xml", "text/xml"];
const DXFMimeTypes = ["image/vnd.dxf"];
const CadMimeTypes = [
  "model/example",
  "model/iges",
  "model/mesh",
  "model/vrml",
  "model/x3d+binary",
  "model/x3d+vrml",
  "model/x3d+xml",
  "image/vnd.dwg",
];
const TextMimeTypes = [
  "text/cmd",
  "text/css",
  "text/html",
  "text/javascript",
  "text/plain",
  "text/php",
  "text/markdown",
  "text/cache-manifest",
];
const TiffMimeTypes = ["image/tiff"];
const TtfMimeTypes = [
  "font/sfnt",
  "font/ttf",
  "application/font-sfnt",
  "application/x-font-ttf",
];
const CSVMimeTypes = ["text/csv"];
const VcardMimeTypes = ["text/vcard", "text/x-vcard"];
const SVGMimeTypes = ["image/svg+xml"];
const EPSMimeTypes = [
  "image/x-eps", // eps
  "application/postscript", // eps / ps
];
const PhotoshopMimeTypes = [
  "image/vnd.adobe.photoshop", // psd
  "application/x-photoshop",
  "application/photoshop",
  "application/psd",
  "image/psd",
];
const IllustratorMimeTypes = [
  "application/illustrator", // ai
];
const SketchMimeTypes = [
  "application/x-sqlite3", // sketch
];
const XPSMimeTypes = ["application/vnd.ms-xpsdocument", "application/oxps"];
const ICSMimeTypes = ["text/calendar"];

function convertToDictionary<TKey extends string, TValue>(
  array: TKey[],
  value: TValue,
): Record<TKey, TValue> {
  const result = {} as Record<TKey, TValue>;
  array.forEach((key) => {
    result[key] = value;
  });
  return result;
}

const mimeTypesMap: Partial<Record<string, FileType>> = Object.assign(
  convertToDictionary(ArchiveMimeTypes, "archive"),
  convertToDictionary(CadMimeTypes, "cad"),
  convertToDictionary(CSVMimeTypes, "csv"),
  convertToDictionary(DXFMimeTypes, "dxf"),
  convertToDictionary(EPSMimeTypes, "eps"),
  convertToDictionary(GifMimeTypes, "gif"),
  convertToDictionary(ICSMimeTypes, "ics"),
  convertToDictionary(IllustratorMimeTypes, "ai"),
  convertToDictionary(ImageMimeTypes, "image"),
  convertToDictionary(AudioMimeTypes, "audio"),
  convertToDictionary(OpenDocumentMimeTypes, "gsheet"),
  convertToDictionary(PdfMimeTypes, "pdf"),
  convertToDictionary(PhotoshopMimeTypes, "psd"),
  convertToDictionary(PowerpointMimeTypes, "powerpoint"),
  convertToDictionary(SpreadsheetMimeTypes, "spreadsheet"),
  convertToDictionary(SVGMimeTypes, "svg"),
  convertToDictionary(TextMimeTypes, "text"),
  convertToDictionary(TiffMimeTypes, "tiff"),
  convertToDictionary(TtfMimeTypes, "ttf"),
  convertToDictionary(VcardMimeTypes, "vcard"),
  convertToDictionary(VideoMimeTypes, "video"),
  convertToDictionary(WordMimeTypes, "word"),
  convertToDictionary(XmlMimeTypes, "xml"),
  convertToDictionary(XPSMimeTypes, "xps"),
  convertToDictionary(SketchMimeTypes, "sketch"),
) as Partial<Record<string, FileType>>;
